import useAccountQuery from 'api/queries/useAccountQuery';
import { usePlusMembershipLifetimeSavingsQuery } from 'api/queries/usePlusMembershipLifetimeSavingsQuery';
import { usePlusMembershipOptionsQuery } from 'api/queries/usePlusMembershipOptionsQuery';
import {
    AVERAGE_MEMBERSHIP_SAVINGS,
    BRANDING_NAME,
    CARRIER_SHIP_FEE_REDUCTION,
    MEMBERSHIP_COST,
    SHORT_BRANDING_NAME,
    TRIAL_LENGTH_DAYS,
} from 'constants/plusMembership';
import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import useGlobalStore from 'hooks/useGlobalStore';

export function useAccountPlusMembershipHook() {
    const { isMisfitsTheme } = useGlobalStore();
    const brandDomain = isMisfitsTheme ? MISFITS_THEME_DOMAIN : IMPERFECT_THEME_DOMAIN;

    const plusMembershipBranding = BRANDING_NAME[brandDomain];
    const plusMembershipShortBranding = SHORT_BRANDING_NAME[brandDomain];

    const { data: account = {} } = useAccountQuery();
    const { data: plusMembershipOptionsQueryData = {}, isLoading: isPlusMembershipOptionsLoading } =
        usePlusMembershipOptionsQuery();

    // v2: prefer eligibleMembershipTerm (membershipEndDate, membershipPrice); fall back to top-level options (v1)
    const eligibleMembershipTermQueryData = plusMembershipOptionsQueryData?.eligibleMembershipTerm;
    const eligibleMembershipTerm = eligibleMembershipTermQueryData
        ? {
              typeOfMembership: eligibleMembershipTermQueryData.typeOfMembership,
              membershipDuration: eligibleMembershipTermQueryData.membershipDuration,
              membershipStartDate: eligibleMembershipTermQueryData.membershipStartDate,
              membershipRenewalDate: eligibleMembershipTermQueryData.membershipEndDate,
              price: eligibleMembershipTermQueryData.membershipPrice,
              perks: eligibleMembershipTermQueryData.perks,
              plusMembershipVariantID: eligibleMembershipTermQueryData.plusMembershipVariantID,
          }
        : plusMembershipOptionsQueryData;

    const averageMembershipSavingsPeriod = 'year';
    const averageMembershipSavings =
        AVERAGE_MEMBERSHIP_SAVINGS[account?.isDeliveredByImperfect ? 'dbi' : 'cs'][averageMembershipSavingsPeriod];

    const isPlusMembershipAvailable =
        eligibleMembershipTerm?.typeOfMembership === 'PAID' && (account?.plusMemberships ?? []).length === 0;
    const isTrialMembershipAvailable =
        eligibleMembershipTerm?.typeOfMembership === 'FREE_TRIAL' && (account?.plusMemberships ?? []).length === 0;
    const plusMembershipFee = eligibleMembershipTerm?.price ?? MEMBERSHIP_COST;
    const earlyRenewalOffer = plusMembershipOptionsQueryData?.earlyRenewalOffer || {};

    let renewalCost = plusMembershipFee;
    let renewalDiscountCopy = '';

    if (Object.keys(earlyRenewalOffer).length > 0) {
        renewalCost = earlyRenewalOffer?.discountPrice;
        if (earlyRenewalOffer?.discountType === 'dollar') {
            // Returned as whole dollars
            renewalDiscountCopy = `$${earlyRenewalOffer.discountAmount}`;
        } else if (earlyRenewalOffer?.discountType === 'percent') {
            // Returned as whole percent (out of 100)
            renewalDiscountCopy = `${earlyRenewalOffer.discountAmount}%`;
        }
    }

    const shippingFees =
        account && account.shippingFee
            ? {
                  plusMembershipShippingFee: account.isDeliveredByImperfect
                      ? 0
                      : account.shippingFee - CARRIER_SHIP_FEE_REDUCTION,
                  plusMembershipShippingSavings: account.isDeliveredByImperfect
                      ? account.shippingFee
                      : CARRIER_SHIP_FEE_REDUCTION,
              }
            : null;

    const plusMemberships = account?.plusMemberships ?? [];
    const customerTrial = plusMemberships.find(
        (plusMembership) => plusMembership.term.typeOfMembership === 'FREE_TRIAL'
    );
    const customerPaidMembership = plusMemberships.find(
        (plusMembership) => plusMembership.term.typeOfMembership === 'PAID'
    );

    const genericMembership = customerPaidMembership ?? customerTrial;

    const sharedMembershipValues = !!genericMembership && {
        memberSinceDate: `${genericMembership?.memberSince?.date ?? genericMembership.term.startDate.date}`,
        startDate: `${genericMembership.term.startDate.date}`,
        endDate: `${genericMembership.term.endDate.date}`,
        isAutoRenew: genericMembership.term.isAutoRenew,
        status: genericMembership.term.status,
        price: genericMembership.variant.price,
        paidDurationDays: genericMembership.variant.paidDurationDays,
        variantId: genericMembership.variant.id,
        amountCharged: genericMembership.term.amountCharged,
    };

    const trial =
        customerTrial && !customerPaidMembership
            ? {
                  ...sharedMembershipValues,
                  trialDurationDays: customerTrial.variant.freeTrialDurationDays,
              }
            : null;

    const membership = customerPaidMembership
        ? { ...sharedMembershipValues, isAutoRenewEnabled: customerPaidMembership.term.isAutoRenew }
        : null;

    const plusMembershipEligibleOrder = account?.nextOrder?.plusMemberPriceEligible;

    const isPlusMember = !!membership;
    const freeTrialExpirationDate = trial?.endDate;

    let membershipStatus = 'eligible';

    if (isPlusMember) {
        membershipStatus = 'active';
    } else if (freeTrialExpirationDate) {
        membershipStatus = 'trial';
    }

    const availableCredits = {
        credits: plusMembershipOptionsQueryData?.newMemberOffer?.credits,
        creditsExpirationDays: plusMembershipOptionsQueryData?.newMemberOffer?.creditsExpirationDays,
    };

    const isMemberDealsRoute = window.location.pathname === '/shop/aisle/member-deals';
    const isAccountRoute = window.location.pathname === '/account';
    const isMembershipRoute = window.location.pathname === '/account/membership';
    const isSavingsTrackerExposurePath = isMemberDealsRoute || isAccountRoute || isMembershipRoute;

    const shouldFetchLifetimeSavings = isPlusMember && isSavingsTrackerExposurePath;

    const { data: plusMembershipLifetimeSavingsQueryData = {}, isLoading: isPlusMembershipLifetimeSavingsLoading } =
        usePlusMembershipLifetimeSavingsQuery(shouldFetchLifetimeSavings);

    const memberSavings =
        (plusMembershipLifetimeSavingsQueryData.totalPlusItemSavings || 0) +
        (plusMembershipLifetimeSavingsQueryData.totalPlusShippingSavings || 0);

    const isSavingsTrackerEnabled =
        shouldFetchLifetimeSavings && !isPlusMembershipLifetimeSavingsLoading && memberSavings >= 1;

    return {
        averageMembershipSavings,
        averageMembershipSavingsPeriod,
        plusMembershipBranding,
        plusMembershipShortBranding,
        isPlusMembershipOptionsLoading,
        isSavingsTrackerEnabled,
        memberSavings,
        availableCredits,
        availableTrialMembership: isTrialMembershipAvailable
            ? {
                  trialDurationDays: eligibleMembershipTerm?.membershipDuration ?? TRIAL_LENGTH_DAYS,
                  variantId: eligibleMembershipTerm?.plusMembershipVariantID,
              }
            : null,
        availablePlusMembership: isPlusMembershipAvailable
            ? {
                  membershipDurationDays: eligibleMembershipTerm?.membershipDuration ?? TRIAL_LENGTH_DAYS,
                  variantId: eligibleMembershipTerm?.plusMembershipVariantID,
              }
            : null,
        plusMembershipFee,
        shippingFees,
        trial,
        membership,
        newMemberOffer: plusMembershipOptionsQueryData?.newMemberOffer || null,
        price: {
            annual: genericMembership?.variant?.price ?? eligibleMembershipTerm?.price ?? MEMBERSHIP_COST,
        },
        plusMembershipEligibleOrder,
        membershipStatus,
        membershipRenewalDate: eligibleMembershipTerm?.membershipRenewalDate,
        membershipExpirationDate: sharedMembershipValues?.endDate,
        renewalCost,
        renewalDiscountCopy,
    };
}
